/**
 * Single source of truth for what a customer is told when a call does not
 * connect or ends unexpectedly (LOVABLE_BRIEF_call_failure_messages.md,
 * 12 September 2026).
 *
 * Rules encoded here:
 *  - one plain sentence, one action, never a code in the sentence;
 *  - the raw cause stays available for a "Details" disclosure and the console;
 *  - "You weren't charged." only on rows the billing guard makes true.
 *
 * iOS (CallFailureCopy.swift) and Android (CallFailureCopy.kt) carry the same
 * table; change the words here first, then mirror them.
 */

export type CallFailureAction =
  | 'none'
  | 'call_again'
  | 'try_again'
  | 'try_again_later'
  | 'try_again_minutes'
  | 'check_number'
  | 'add_credit';

export type CallFailureCharged = 'no' | 'as_timed';

export interface CallFailureCopy {
  /** Short bold lead phrase, e.g. "Line busy". */
  lead: string;
  /** One plain sentence the customer can act on. */
  sentence: string;
  action: CallFailureAction;
  charged: CallFailureCharged;
  /** Raw `cause name (code)` for the Details disclosure and support. */
  details: string;
}

export const CALL_FAILURE_ACTION_LABEL: Record<CallFailureAction, string | null> = {
  none: null,
  call_again: 'Call again',
  try_again: 'Try again',
  try_again_later: 'Try again later',
  try_again_minutes: 'Try again in a few minutes',
  check_number: 'Check the number',
  add_credit: 'Add credit',
};

interface Row {
  /** FreeSWITCH cause names (upper case) this row owns. */
  causes?: string[];
  /** Q.850 numbers this row owns. */
  q850?: number[];
  /** SIP response codes this row owns. */
  sip?: number[];
  /** Free-text patterns (lower-cased raw string match). */
  match?: RegExp;
  lead: string;
  sentence: string;
  action: CallFailureAction;
  charged: CallFailureCharged;
}

const TABLE: Row[] = [
  {
    causes: ['NORMAL_CLEARING'], q850: [16],
    lead: 'Call ended', sentence: 'The call ended.', action: 'none', charged: 'as_timed',
  },
  {
    causes: ['ORIGINATOR_CANCEL'], sip: [487],
    lead: 'Call cancelled', sentence: 'You ended the call before it connected.', action: 'call_again', charged: 'no',
  },
  {
    causes: ['USER_BUSY'], q850: [17], sip: [486],
    lead: 'Line busy', sentence: "They're on another call right now.", action: 'try_again_minutes', charged: 'no',
  },
  {
    causes: ['NO_ANSWER', 'NO_USER_RESPONSE'], q850: [18, 19], sip: [480],
    lead: 'No answer', sentence: 'Nobody picked up.', action: 'try_again_later', charged: 'no',
  },
  {
    causes: ['CALL_REJECTED'], q850: [21], sip: [603],
    lead: 'Call declined', sentence: 'The call was declined at the other end.', action: 'try_again_later', charged: 'no',
  },
  {
    causes: ['UNALLOCATED_NUMBER'], q850: [1], sip: [404],
    lead: 'Number not in service', sentence: "That number isn't in service. Check the digits and the country code.", action: 'check_number', charged: 'no',
  },
  {
    causes: ['INVALID_NUMBER_FORMAT'], q850: [28], sip: [484],
    lead: 'Number incomplete', sentence: "That number doesn't look complete. Check the country code and try again.", action: 'check_number', charged: 'no',
  },
  {
    causes: ['NUMBER_CHANGED'], q850: [22],
    lead: 'Number changed', sentence: 'That number has changed. Ask them for the new one.', action: 'none', charged: 'no',
  },
  {
    causes: ['NORMAL_TEMPORARY_FAILURE'], q850: [41],
    lead: "Didn't connect", sentence: 'A temporary network problem stopped the call. This usually clears in a moment.', action: 'try_again', charged: 'no',
  },
  {
    causes: ['RECOVERY_ON_TIMER_EXPIRE'], q850: [102], sip: [408],
    lead: "Didn't connect", sentence: "We couldn't reach the network in time. Check your connection and try again.", action: 'try_again', charged: 'no',
  },
  {
    causes: ['NETWORK_OUT_OF_ORDER', 'DESTINATION_OUT_OF_ORDER'], q850: [27, 38], sip: [502],
    lead: 'Network busy', sentence: "The network at the other end isn't reachable right now.", action: 'try_again_minutes', charged: 'no',
  },
  {
    causes: ['NORMAL_CIRCUIT_CONGESTION', 'SWITCH_CONGESTION'], q850: [34, 42], sip: [503],
    match: /congest|termination is not available|max.?channels|call limit|previous call is still ending/i,
    lead: 'Network busy', sentence: 'The route is busy right now.', action: 'try_again_minutes', charged: 'no',
  },
  {
    causes: ['SERVICE_UNAVAILABLE', 'GATEWAY_DOWN'], q850: [63], sip: [500],
    lead: "Didn't connect", sentence: "elefa's network had a hiccup. Please try again in a moment.", action: 'try_again', charged: 'no',
  },
  {
    causes: ['NO_ROUTE_DESTINATION', 'NO_ROUTE_TRANSIT_NET'], q850: [3],
    match: /no route|dest_unsupported|unsupported destination/i,
    lead: 'Not available yet', sentence: "elefa doesn't connect calls to this destination yet.", action: 'none', charged: 'no',
  },
  {
    causes: ['BEARER_CAPABILITY_NOTAUTH'], q850: [57], sip: [403],
    lead: "Can't call this number", sentence: "This number can't be called from elefa.", action: 'none', charged: 'no',
  },
  {
    causes: ['ALLOTTED_TIMEOUT'],
    match: /max.?call.?seconds|allotted/i,
    lead: 'Credit used up', sentence: 'The call ended because your credit ran out.', action: 'add_credit', charged: 'as_timed',
  },
  {
    causes: ['MEDIA_TIMEOUT'],
    match: /rtp timeout|media timeout/i,
    lead: 'Connection lost', sentence: 'The call dropped because the connection was lost.', action: 'try_again', charged: 'as_timed',
  },
  {
    causes: ['MANAGER_REQUEST', 'SYSTEM_SHUTDOWN'],
    lead: "Didn't connect", sentence: "elefa's network had a hiccup. Please try again in a moment.", action: 'try_again', charged: 'no',
  },
];

const CREDIT_ROW: Omit<Row, 'causes'> = {
  lead: 'Out of credit',
  sentence: "You don't have enough credit to start this call.",
  action: 'add_credit',
  charged: 'no',
};

const FALLBACK: Omit<Row, 'causes'> = {
  lead: "Didn't connect",
  sentence: "The call couldn't be completed.",
  action: 'try_again',
  charged: 'no',
};

function normalise(raw: string | null | undefined): string {
  return (raw || '').trim();
}

/** Pull a Q.850 / cause name out of a mixed raw string, e.g. "USER_BUSY (17)". */
function causeToken(raw: string): string {
  const m = raw.toUpperCase().match(/[A-Z_]{4,}/);
  return m ? m[0] : '';
}

function q850From(raw: string): number | null {
  const m = raw.match(/\((\d{1,3})\)/) || raw.match(/\b(\d{1,3})\b/);
  return m ? Number(m[1]) : null;
}

/**
 * Describe a call failure in the customer's words.
 *
 * @param cause   FreeSWITCH hangup cause / raw failure text.
 * @param sipCode SIP response code where one applies.
 * @param connected Whether media ever connected (affects the charged line only).
 */
export function describeCallFailure(
  cause: string | null | undefined,
  sipCode?: number | null,
  connected?: boolean,
): CallFailureCopy {
  const raw = normalise(cause);
  const token = causeToken(raw);
  const numeric = q850From(raw);

  // Billing guard rejection reads as "no credit" regardless of the SIP code.
  const isCreditFailure = /insufficient credit|no credit|top.?up|INSUFFICIENT_CREDIT/i.test(raw);

  let row: Omit<Row, 'causes'> | undefined;
  if (isCreditFailure) {
    row = CREDIT_ROW;
  } else {
    row = TABLE.find((r) => (token && r.causes?.includes(token)))
      || TABLE.find((r) => (sipCode != null && r.sip?.includes(sipCode)))
      || TABLE.find((r) => (r.match ? r.match.test(raw) : false))
      || (numeric != null ? TABLE.find((r) => r.q850?.includes(numeric)) : undefined)
      || FALLBACK;
  }

  const detailCode = sipCode != null ? `SIP ${sipCode}` : numeric != null ? `${numeric}` : '';
  const details = [token || raw || 'UNKNOWN', detailCode].filter(Boolean).join(' · ');

  return {
    lead: row.lead,
    sentence: row.sentence,
    action: row.action,
    // A call that never connected is never charged, whatever the row says.
    charged: connected === false ? 'no' : row.charged,
    details,
  };
}

/** Recents shows the lead only, derived from the stored call status. */
export function leadForCallStatus(status: string): string | null {
  switch (status) {
    case 'no_answer': return 'No answer';
    case 'busy': return 'Line busy';
    case 'rejected': return 'Call declined';
    case 'missed': return 'Missed';
    case 'cancelled': return 'Call cancelled';
    case 'failed': return "Didn't connect";
    default: return null;
  }
}

/** One-line string for toasts, where a sheet is not available. */
export function callFailureLine(copy: CallFailureCopy): string {
  const action = CALL_FAILURE_ACTION_LABEL[copy.action];
  return action ? `${copy.lead}. ${copy.sentence} ${action}.` : `${copy.lead}. ${copy.sentence}`;
}
